import React from 'react';
import { Branding } from '../Domain/clientPortalService';
import LoadingAnimation from '../../../shared/LoadingAnimation';
import styles from './ClientPortalPreview.module.scss';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from "ui-builder";
import { useDataLoaderNew } from 'data-loader';
import { FileType, useFileService } from "nekst-api";

type PlatformMode = 'web' | 'mobile';

interface ClientPortalPreviewProps {
  branding: Branding;
  platformMode?: PlatformMode;
  showSwitcher: boolean;
}

function useUrlBuilder() {
  const fileService = useFileService();

  const build = async (branding: Branding, platformMode: PlatformMode) => {
    const params = new URLSearchParams();

    const theme = branding.theme;

    if (platformMode === 'mobile') {
      params.append('mobile', '1');
    }

    if (branding?._logoUrl?.resized) {
      params.append('logo', branding._logoUrl.resized);
    } else if (branding?.logoFileId) {
      const logoFile = await fileService.getFileById(FileType.COMPANY_LOGO, branding.logoFileId);
      params.append('logo', process.env.REACT_APP_API_URL + '/files/branding/resized/' + logoFile.alias)
    } else {
      params.append('logo', 'default');
    }

    if (branding?._mobileLogoUrl?.resized) {
      params.append('mobileLogo', branding._mobileLogoUrl.resized);
    } else if (branding?.mobileLogoFileId) {
      const mobileLogoFile = await fileService.getFileById(FileType.COMPANY_LOGO, branding.mobileLogoFileId);
      params.append('mobileLogo', process.env.REACT_APP_API_URL + '/files/branding/resized/' + mobileLogoFile.alias)
    } else {
      params.append('mobileLogo', 'default');
    }

    if (theme.coloredBackgroundBlock.backgroundColor) {
      params.append('cBg', theme.coloredBackgroundBlock.backgroundColor);
    }
    if (theme.coloredBackgroundBlock.fontColor) {
      params.append('cFont', theme.coloredBackgroundBlock.fontColor);
    }
    if (theme.coloredBackgroundBlock.shadingColor) {
      params.append('cShade', theme.coloredBackgroundBlock.shadingColor);
    }
    if (theme.coloredBackgroundBlock.highlightColor) {
      params.append('cHighlight', theme.coloredBackgroundBlock.highlightColor);
    }
    if (theme.coloredBackgroundBlock.cardFontColor) {
      params.append('cCardFont', theme.coloredBackgroundBlock.cardFontColor);
    }

    if (theme.whiteBackgroundBlock.backgroundColor) {
      params.append('wBg', theme.whiteBackgroundBlock.backgroundColor);
    }
    if (theme.whiteBackgroundBlock.fontColor) {
      params.append('wFont', theme.whiteBackgroundBlock.fontColor);
    }
    if (theme.whiteBackgroundBlock.shadingColor) {
      params.append('wShade', theme.whiteBackgroundBlock.shadingColor);
    }
    if (theme.whiteBackgroundBlock.highlightColor) {
      params.append('wHighlight', theme.whiteBackgroundBlock.highlightColor);
    }
    if (theme.whiteBackgroundBlock.cardFontColor) {
      params.append('wCardFont', theme.whiteBackgroundBlock.cardFontColor);
    }

    if (theme.grayBackgroundBlock.backgroundColor) {
      params.append('gBg', theme.grayBackgroundBlock.backgroundColor);
    }
    if (theme.grayBackgroundBlock.fontColor) {
      params.append('gFont', theme.grayBackgroundBlock.fontColor);
    }
    if (theme.grayBackgroundBlock.shadingColor) {
      params.append('gShade', theme.grayBackgroundBlock.shadingColor);
    }
    if (theme.grayBackgroundBlock.highlightColor) {
      params.append('gHighlight', theme.grayBackgroundBlock.highlightColor);
    }
    if (theme.grayBackgroundBlock.cardFontColor) {
      params.append('gCardFont', theme.grayBackgroundBlock.cardFontColor);
    }

    const baseUrl = 'https://nekst.nekst.com/nQJANJ';
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  return {
    build,
  };
}


export default function ClientPortalPreview(props: ClientPortalPreviewProps) {
  const [isLoadingIframe, setIsLoadingIframe] = React.useState(true);
  const [hasIframeError, setHasIframeError] = React.useState(false);

  const urlBuilder = useUrlBuilder();

  const [platformMode, setPlatformMode] = React.useState<PlatformMode>(props.platformMode || 'web');

  const handleWebClick = React.useCallback(() => {
    setPlatformMode('web');
  }, []);

  const handleMobileClick = React.useCallback(() => {
    setPlatformMode('mobile');
  }, []);

  const urlLoader = useDataLoaderNew(
    async () => {
      return await urlBuilder.build(props.branding, platformMode);
    },
    {
      dependencies: [props.branding.theme, platformMode, props.branding]
    },
  );

  React.useEffect(() => {
    setIsLoadingIframe(true);
    setHasIframeError(false);
  }, [urlLoader.data]);

  const handleLoad = React.useCallback(() => {
    setIsLoadingIframe(false);
  }, []);

  const handleError = React.useCallback(() => {
    setIsLoadingIframe(false);
    setHasIframeError(true);
  }, []);

  const iframeClassName = platformMode === 'mobile'
    ? styles.iframeMobile
    : styles.iframeWeb;

  const isLoading = urlLoader.loading || isLoadingIframe;

  return (
    <div className={styles.previewPopupContent}>
      {props.showSwitcher && (
        <div className={styles.platformSwitcher}>
          <span className={styles.platformLabel}>Device Preview:</span>
          <Button
            text="WEB"
            color={platformMode === 'web' ? ButtonColor.BLUE : ButtonColor.GRAY}
            size={ButtonSize.SMALL}
            variant={platformMode === 'web' ? ButtonVariant.FILLED : ButtonVariant.OUTLINE}
            onClick={handleWebClick}
          />
          <Button
            text="MOBILE"
            color={platformMode === 'mobile' ? ButtonColor.BLUE : ButtonColor.GRAY}
            size={ButtonSize.SMALL}
            variant={platformMode === 'mobile' ? ButtonVariant.FILLED : ButtonVariant.OUTLINE}
            onClick={handleMobileClick}
          />
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.iframeWrapper}>
          {isLoading && (
            <div className={styles.loadingOverlay}>
              <LoadingAnimation />
            </div>
          )}
          {hasIframeError && (
            <div className={styles.errorMessage}>
              Preview unavailable. Please check your network connection.
            </div>
          )}
          <div className={iframeClassName}>
            {!hasIframeError && urlLoader.data && (
              <iframe
                src={urlLoader.data}
                className={styles.iframe}
                title="Client Portal Preview"
                onLoad={handleLoad}
                onError={handleError}
                sandbox="allow-scripts allow-same-origin"
                style={{ display: isLoading ? 'none' : 'block' }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
